import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Custom auth lock with a hard timeout.
//
// supabase-js v2 uses navigator.locks by default to coordinate auth-token
// refresh across browser tabs. The lock is acquired before any auth-aware
// operation (data queries, refreshSession, etc.) — and when a tab is
// hidden the browser may throttle/pause the task that's holding the lock.
// On tab return, supabase queues every new query behind the same lock,
// the holder never gets to release it cleanly, and we end up with
// fetchOrders sitting forever on a request that never goes out.
//
// This replaces the default lock with one that races navigator.locks
// against a 3s timer. If the OS-level lock can't be acquired in 3s, we
// fall through and run the work anyway. Worst case: a brief auth race
// across tabs (rare here — internal dispatch app, mostly single-tab).
// Far better than the alternative: queries hanging forever.
async function timeoutLock<R>(name: string, acquireTimeout: number, fn: () => Promise<R>): Promise<R> {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return fn()
  }
  const cap = Math.min(acquireTimeout || 3000, 3000)
  let locked = false
  const acquired = new Promise<R>((resolve, reject) => {
    navigator.locks.request(name, { mode: 'exclusive' }, async () => {
      locked = true
      try { resolve(await fn()) } catch (e) { reject(e) }
    }).catch(reject)
  })
  const fallback = new Promise<R>((resolve, reject) => {
    setTimeout(() => {
      if (locked) return
      // Lock not acquired in time — proceed without it.
      console.warn(`[supabase] auth lock "${name}" timed out after ${cap}ms; running unlocked`)
      fn().then(resolve, reject)
    }, cap)
  })
  return Promise.race([acquired, fallback])
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    lock: timeoutLock,
  },
})
